/**
 * /sell/integrations — Brief K supplier UI.
 *
 * Lists every integration the supplier has registered, lets them
 * register new ones via a wizard, edit field maps, run manual polls,
 * pause/resume, and act on pending removals.
 *
 * Server enforces role gating; this page handles the 403 by showing a
 * sign-in CTA, same pattern as SellerMyStock.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { AppLayout } from '../components/AppLayout'
import { api } from '../api/client'
import { useApp } from '../store'

// Canonical fields available in the dropdown of the field-map editor.
// Keep in sync with functions/src/chat/prompts/integration-field-map.ts.
const CANONICAL_FIELDS = [
  '— unmapped —',
  'stockId',
  'shape',
  'color',
  'fancyColor',
  'clarity',
  'cut',
  'polish',
  'symmetry',
  'fluorescence',
  'lab',
  'certNo',
  'carat',
  'pricePerCarat',
  'priceUSD',
  'imageUrl',
  'videoUrl',
  'length',
  'width',
  'height',
  'ratio',
  'heartsArrows',
] as const

type IntegrationKind = 'https' | 's3' | 'dropbox' | 'folder-of-images' | 'firestore-pull' | 'gdrive' | 'ftp'

interface IntegrationDoc {
  integrationId: string
  userId: number
  kind: IntegrationKind
  config: Record<string, unknown>
  fieldMap: Record<string, string | null>
  sampleRow?: Record<string, unknown> | null
  cadence: { hours: number }
  status: 'discovering' | 'awaiting-confirmation' | 'active' | 'paused' | 'failed'
  lastPollAt?: string | null
  lastPollStatus?: 'ok' | 'error' | null
  lastPollNote?: string | null
  nextPollDue?: string | null
  label?: string | null
  createdAt: string
  updatedAt: string
}

interface DiscoverResponse {
  candidateFieldMap: Record<string, string | null>
  sampleRows: Record<string, unknown>[]
  geminiUsed: boolean
  note?: string
}

interface IntegrationLog {
  logId: string
  ts: string
  added: number
  updated: number
  removedQueued: number
  imageAttached?: number
  errors: Array<{ stockId?: string; message: string }>
  durationMs: number
  trigger: string
}

interface PendingRemoval {
  stockId: string
  integrationId: string
  queuedAt: string
  missCount: number
}

export function SellerIntegrations() {
  const params = useParams()
  if (params.id) return <IntegrationDetail integrationId={params.id} />
  return <IntegrationsList />
}

// ─── List view ──────────────────────────────────────────────────────

function IntegrationsList() {
  const signedIn = useApp((s) => s.signedIn)
  const [integrations, setIntegrations] = useState<IntegrationDoc[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showWizard, setShowWizard] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await api<{ integrations: IntegrationDoc[] }>('/integrations')
      setIntegrations(r.integrations || [])
      setError(null)
    } catch (e) {
      const err = e as { status?: number; message?: string }
      if (err.status === 403) setError('Supplier or admin access required.')
      else setError(err.message || 'Failed to load')
    } finally { setLoading(false) }
  }, [])

  useEffect(() => {
    if (signedIn) load()
    else { setLoading(false); setError('Sign in as a supplier to manage integrations.') }
  }, [signedIn, load])

  return (
    <AppLayout>
      <section className="mx-auto max-w-5xl px-6 py-10">
        <div className="flex items-end justify-between gap-4 mb-8 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">Supplier · integrations</p>
            <h1 className="text-3xl font-bold text-ink mt-2">Auto-sync your inventory</h1>
            <p className="text-sm text-muted mt-1 max-w-xl">
              Connect a JSON endpoint, S3 bucket, or Dropbox folder. DiaEx reads it,
              proposes a field map, and keeps your inventory in sync without manual uploads.
            </p>
          </div>
          {!showWizard && (
            <button
              type="button"
              onClick={() => setShowWizard(true)}
              className="btn-primary"
              data-testid="integrations-add"
            >
              Add integration
            </button>
          )}
        </div>

        {showWizard && (
          <Wizard
            onCancel={() => setShowWizard(false)}
            onCreated={() => { setShowWizard(false); load() }}
          />
        )}

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[0, 1].map((i) => <div key={i} className="card animate-pulse h-32" />)}
          </div>
        ) : error ? (
          <p className="text-sm text-danger">{error}</p>
        ) : integrations.length === 0 && !showWizard ? (
          <div className="card text-center py-12">
            <p className="text-sm text-muted">No integrations yet. Click <strong>Add integration</strong> to connect your first source.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            {integrations.map((it) => (
              <IntegrationCard key={it.integrationId} integration={it} />
            ))}
          </div>
        )}
      </section>
    </AppLayout>
  )
}

function IntegrationCard({ integration: it }: { integration: IntegrationDoc }) {
  return (
    <Link
      to={`/sell/integrations/${it.integrationId}`}
      className="card-hover block"
      data-testid="integration-card"
      data-integration-id={it.integrationId}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-soft">{it.kind}</p>
          <p className="text-base font-semibold text-ink">{it.label || prettyKind(it.kind)}</p>
        </div>
        <StatusBadge status={it.status} />
      </div>
      <p className="text-xs text-soft">
        Cadence · every {it.cadence.hours}h
      </p>
      <p className="text-xs text-soft mt-1">
        {it.lastPollAt ? `Last poll · ${new Date(it.lastPollAt).toLocaleString()}` : 'No polls yet'}
      </p>
      {it.lastPollNote && (
        <p className="text-[11px] text-soft mt-1 line-clamp-2">{it.lastPollNote}</p>
      )}
    </Link>
  )
}

function StatusBadge({ status }: { status: IntegrationDoc['status'] }) {
  const cls: Record<IntegrationDoc['status'], string> = {
    'discovering': 'bg-blue-100 text-blue-700',
    'awaiting-confirmation': 'bg-amber-100 text-amber-800',
    'active': 'bg-green-100 text-green-700',
    'paused': 'bg-gray-100 text-gray-700',
    'failed': 'bg-red-100 text-red-700',
  }
  return (
    <span className={`text-[10px] uppercase tracking-wide px-2 py-1 rounded-full font-semibold ${cls[status]}`}>
      {status}
    </span>
  )
}

function prettyKind(k: IntegrationKind): string {
  switch (k) {
    case 'https': return 'HTTPS endpoint'
    case 's3': return 'AWS S3'
    case 'dropbox': return 'Dropbox'
    case 'folder-of-images': return 'Folder of images'
    case 'firestore-pull': return 'Firestore (pull)'
    case 'gdrive': return 'Google Drive'
    case 'ftp': return 'FTP server'
  }
}

// ─── Wizard (multi-step) ────────────────────────────────────────────

type WizardStep = 'kind' | 'config' | 'discovery' | 'cadence'

interface WizardProps {
  onCancel: () => void
  onCreated: () => void
}

function Wizard({ onCancel, onCreated }: WizardProps) {
  const [step, setStep] = useState<WizardStep>('kind')
  const [kind, setKind] = useState<IntegrationKind | null>(null)
  const [config, setConfig] = useState<Record<string, string>>({})
  const [label, setLabel] = useState('')
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [created, setCreated] = useState<IntegrationDoc | null>(null)
  const [discovery, setDiscovery] = useState<DiscoverResponse | null>(null)
  const [editedMap, setEditedMap] = useState<Record<string, string | null>>({})
  const [cadenceHours, setCadenceHours] = useState(4)

  const onCreate = async () => {
    if (!kind) return
    setCreating(true); setError(null)
    try {
      const body = {
        kind,
        config: parseConfig(kind, config),
        cadenceHours,
        label: label || undefined,
      }
      const r = await api<{ integration: IntegrationDoc; discover?: DiscoverResponse }>('/integrations', {
        method: 'POST',
        body,
      })
      setCreated(r.integration)
      if (r.discover) {
        setDiscovery(r.discover)
        setEditedMap(r.discover.candidateFieldMap)
      }
      setStep('discovery')
    } catch (e) {
      const err = e as { status?: number; message?: string; data?: { message?: string } }
      setError(err.data?.message || err.message || 'Create failed')
    } finally { setCreating(false) }
  }

  const onConfirm = async () => {
    if (!created) return
    setCreating(true); setError(null)
    try {
      await api(`/integrations/${created.integrationId}/confirm`, {
        method: 'POST',
        body: { fieldMap: editedMap, cadenceHours },
      })
      onCreated()
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || 'Confirm failed')
    } finally { setCreating(false) }
  }

  return (
    <div className="card mb-6" data-testid="integrations-wizard">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-semibold text-ink">
          New integration
          <span className="ml-2 text-xs text-soft font-normal">step {wizardStepNum(step)} of 4</span>
        </p>
        <button onClick={onCancel} className="btn-ghost text-xs">Cancel</button>
      </div>

      {error && (
        <p className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2">{error}</p>
      )}

      {step === 'kind' && (
        <div data-testid="wizard-step-kind">
          <p className="text-xs text-muted mb-3">Pick the source type for your inventory:</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <KindCard k="https" label="HTTPS endpoint" desc="DiaEx polls a JSON URL on a schedule." selected={kind === 'https'} onPick={() => { setKind('https'); setStep('config') }} />
            <KindCard k="s3" label="AWS S3" desc="JSON file or folder of images in your bucket." selected={kind === 's3'} onPick={() => { setKind('s3'); setStep('config') }} />
            <KindCard k="dropbox" label="Dropbox" desc="Shared link or app-folder access." selected={kind === 'dropbox'} onPick={() => { setKind('dropbox'); setStep('config') }} />
            <KindCard k="folder-of-images" label="Folder of images" desc="S3 or Dropbox folder; filenames map to stock IDs." selected={kind === 'folder-of-images'} onPick={() => { setKind('folder-of-images'); setStep('config') }} />
          </div>
        </div>
      )}

      {step === 'config' && kind && (
        <div data-testid="wizard-step-config">
          <ConfigForm
            kind={kind}
            config={config}
            onChange={setConfig}
            label={label}
            onLabelChange={setLabel}
          />
          <div className="flex justify-between mt-6 gap-3">
            <button className="btn-secondary" onClick={() => setStep('kind')}>Back</button>
            <button className="btn-primary" disabled={creating} onClick={onCreate} data-testid="wizard-test-connection">
              {creating ? 'Testing connection…' : 'Test & continue'}
            </button>
          </div>
        </div>
      )}

      {step === 'discovery' && discovery && (
        <div data-testid="wizard-step-fieldmap">
          <p className="text-xs text-muted mb-3">
            We pulled <strong>{discovery.sampleRows.length}</strong> sample row{discovery.sampleRows.length === 1 ? '' : 's'}
            {discovery.geminiUsed ? ' and asked DiaEx AI to suggest a mapping.' : ' (heuristic suggestion — no AI key set).'}
            {' '}Edit if needed, then confirm.
          </p>
          <FieldMapEditor map={editedMap} onChange={setEditedMap} sampleRow={discovery.sampleRows[0]} />
          <div className="flex justify-between mt-6 gap-3">
            <button className="btn-secondary" onClick={() => setStep('cadence')}>Next: cadence</button>
            <button className="btn-primary" disabled={creating} onClick={onConfirm} data-testid="wizard-confirm">
              {creating ? 'Saving…' : 'Save & activate'}
            </button>
          </div>
        </div>
      )}

      {step === 'cadence' && (
        <div data-testid="wizard-step-cadence">
          <p className="text-xs text-muted mb-3">How often should DiaEx pull from your source?</p>
          <CadencePicker value={cadenceHours} onChange={setCadenceHours} />
          <div className="flex justify-between mt-6 gap-3">
            <button className="btn-secondary" onClick={() => setStep('discovery')}>Back</button>
            <button className="btn-primary" disabled={creating} onClick={onConfirm} data-testid="wizard-save">
              {creating ? 'Saving…' : 'Save & activate'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function wizardStepNum(s: WizardStep): number {
  return ({ kind: 1, config: 2, discovery: 3, cadence: 4 })[s]
}

function KindCard({ k, label, desc, selected, onPick }: { k: IntegrationKind; label: string; desc: string; selected: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={`text-left p-4 rounded-lg border transition-colors ${selected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/40'}`}
      data-testid={`kind-${k}`}
    >
      <p className="text-sm font-semibold text-ink">{label}</p>
      <p className="text-xs text-soft mt-1">{desc}</p>
    </button>
  )
}

function ConfigForm({ kind, config, onChange, label, onLabelChange }: { kind: IntegrationKind; config: Record<string, string>; onChange: (v: Record<string, string>) => void; label: string; onLabelChange: (v: string) => void }) {
  const set = (k: string, v: string) => onChange({ ...config, [k]: v })
  return (
    <div className="space-y-4">
      <Field label="Label (optional)" hint="A short name so you can tell integrations apart.">
        <input className="input" value={label} onChange={(e) => onLabelChange(e.target.value)} placeholder="e.g. NY warehouse" />
      </Field>
      {kind === 'https' && (
        <>
          <Field label="URL" required>
            <input className="input" value={config.url || ''} onChange={(e) => set('url', e.target.value)} placeholder="https://supplier.example.com/inventory.json" />
          </Field>
          <Field label="Auth">
            <select className="input" value={config.authType || 'none'} onChange={(e) => set('authType', e.target.value)}>
              <option value="none">None</option>
              <option value="bearer">Bearer token</option>
              <option value="basic">Basic (base64 user:pass)</option>
              <option value="header">Custom header</option>
            </select>
          </Field>
          {config.authType === 'header' && (
            <Field label="Header name">
              <input className="input" value={config.authHeader || ''} onChange={(e) => set('authHeader', e.target.value)} placeholder="X-Api-Key" />
            </Field>
          )}
          {config.authType && config.authType !== 'none' && (
            <Field label="Auth value" hint="Stored encrypted at rest.">
              <input className="input" value={config.authValue || ''} onChange={(e) => set('authValue', e.target.value)} placeholder="…" />
            </Field>
          )}
        </>
      )}
      {kind === 's3' && (
        <>
          <Field label="Bucket" required>
            <input className="input" value={config.bucket || ''} onChange={(e) => set('bucket', e.target.value)} placeholder="my-bucket" />
          </Field>
          <Field label="Region">
            <input className="input" value={config.region || ''} onChange={(e) => set('region', e.target.value)} placeholder="us-east-1" />
          </Field>
          <Field label="Object key" required hint="The JSON file inside the bucket (e.g. data/inventory.json).">
            <input className="input" value={config.prefix || ''} onChange={(e) => set('prefix', e.target.value)} placeholder="data/inventory.json" />
          </Field>
          <Field label="Access key id" required>
            <input className="input" value={config.accessKeyId || ''} onChange={(e) => set('accessKeyId', e.target.value)} placeholder="AKIA…" />
          </Field>
          <Field label="Secret access key" required>
            <input className="input" type="password" value={config.secretAccessKey || ''} onChange={(e) => set('secretAccessKey', e.target.value)} />
          </Field>
        </>
      )}
      {kind === 'dropbox' && (
        <>
          <Field label="Shared link to JSON file" hint="Easiest path — paste a Dropbox shared link to your inventory.json.">
            <input className="input" value={config.dropboxSharedLink || ''} onChange={(e) => set('dropboxSharedLink', e.target.value)} placeholder="https://www.dropbox.com/scl/fi/..." />
          </Field>
          <p className="text-[11px] text-soft">— or —</p>
          <Field label="Refresh-token mode (folder access)" hint="Requires a Dropbox app + refresh token. Contact info@thediaex.com if you need help.">
            <input className="input" value={config.dropboxRefreshToken || ''} onChange={(e) => set('dropboxRefreshToken', e.target.value)} placeholder="Refresh token" />
          </Field>
          {config.dropboxRefreshToken && (
            <>
              <Field label="App key">
                <input className="input" value={config.dropboxAppKey || ''} onChange={(e) => set('dropboxAppKey', e.target.value)} />
              </Field>
              <Field label="App secret">
                <input className="input" type="password" value={config.dropboxAppSecret || ''} onChange={(e) => set('dropboxAppSecret', e.target.value)} />
              </Field>
              <Field label="Folder path">
                <input className="input" value={config.dropboxFolderPath || ''} onChange={(e) => set('dropboxFolderPath', e.target.value)} placeholder="/DiaEx/Inventory" />
              </Field>
            </>
          )}
        </>
      )}
      {kind === 'folder-of-images' && (
        <>
          <p className="text-[12px] text-soft">Folder-of-images attaches photos to existing stones by filename. Use either an S3 bucket or a Dropbox folder. Filenames must start with the stock id (e.g. <code>AJ001.jpg</code>).</p>
          <Field label="S3 bucket (optional)">
            <input className="input" value={config.bucket || ''} onChange={(e) => set('bucket', e.target.value)} placeholder="my-bucket" />
          </Field>
          {config.bucket && (
            <>
              <Field label="Region">
                <input className="input" value={config.region || ''} onChange={(e) => set('region', e.target.value)} placeholder="us-east-1" />
              </Field>
              <Field label="Folder prefix">
                <input className="input" value={config.prefix || ''} onChange={(e) => set('prefix', e.target.value)} placeholder="images/stones/" />
              </Field>
              <Field label="Access key id">
                <input className="input" value={config.accessKeyId || ''} onChange={(e) => set('accessKeyId', e.target.value)} />
              </Field>
              <Field label="Secret access key">
                <input className="input" type="password" value={config.secretAccessKey || ''} onChange={(e) => set('secretAccessKey', e.target.value)} />
              </Field>
            </>
          )}
          <Field label="Dropbox folder (optional)">
            <input className="input" value={config.dropboxFolderPath || ''} onChange={(e) => set('dropboxFolderPath', e.target.value)} placeholder="/DiaEx/Photos" />
          </Field>
        </>
      )}
    </div>
  )
}

function Field({ label, required, hint, children }: { label: string; required?: boolean; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-ink">{label}{required && <span className="text-danger ml-0.5">*</span>}</span>
      {hint && <span className="block text-[11px] text-soft mt-0.5">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  )
}

function FieldMapEditor({ map, onChange, sampleRow }: { map: Record<string, string | null>; onChange: (m: Record<string, string | null>) => void; sampleRow?: Record<string, unknown> }) {
  const fields = Object.keys(map)
  const hasStockId = useMemo(() => Object.values(map).includes('stockId'), [map])

  return (
    <div>
      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-page text-xs text-soft uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">Their field</th>
              <th className="text-left px-3 py-2">Sample value</th>
              <th className="text-left px-3 py-2">→ DiaEx field</th>
            </tr>
          </thead>
          <tbody>
            {fields.map((f) => (
              <tr key={f} className="border-t border-border" data-testid="fieldmap-row">
                <td className="px-3 py-2 font-mono text-xs text-ink">{f}</td>
                <td className="px-3 py-2 text-xs text-soft truncate max-w-[200px]">
                  {sampleRow ? truncateValue(sampleRow[f]) : '—'}
                </td>
                <td className="px-3 py-2">
                  <select
                    className="input text-xs py-1"
                    value={map[f] ?? ''}
                    onChange={(e) => onChange({ ...map, [f]: e.target.value || null })}
                    data-testid={`fieldmap-select-${f}`}
                  >
                    {CANONICAL_FIELDS.map((c) => (
                      <option key={c} value={c === '— unmapped —' ? '' : c}>{c}</option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hasStockId && (
        <p className="mt-3 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          ⚠ At least one of your fields must map to <strong>stockId</strong> — DiaEx needs that to identify rows.
        </p>
      )}
    </div>
  )
}

function truncateValue(v: unknown): string {
  if (v === null || v === undefined) return '—'
  const s = typeof v === 'string' ? v : JSON.stringify(v)
  return s.length > 40 ? s.slice(0, 40) + '…' : s
}

function CadencePicker({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const opts = [1, 4, 12, 24]
  return (
    <div className="flex gap-2 flex-wrap">
      {opts.map((h) => (
        <button
          key={h}
          type="button"
          onClick={() => onChange(h)}
          className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${value === h ? 'bg-primary text-white border-primary' : 'border-border hover:border-primary/40'}`}
          data-testid={`cadence-${h}`}
        >
          every {h}h
        </button>
      ))}
    </div>
  )
}

function parseConfig(kind: IntegrationKind, raw: Record<string, string>): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(raw)) {
    if (v === '' || v === undefined || v === null) continue
    out[k] = v
  }
  // Coerce number fields, if any.
  if (kind === 'ftp' && raw.ftpPort) out.ftpPort = Number(raw.ftpPort)
  return out
}

// ─── Detail view ────────────────────────────────────────────────────

function IntegrationDetail({ integrationId }: { integrationId: string }) {
  const navigate = useNavigate()
  const [integration, setIntegration] = useState<IntegrationDoc | null>(null)
  const [logs, setLogs] = useState<IntegrationLog[]>([])
  const [pending, setPending] = useState<PendingRemoval[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editingMap, setEditingMap] = useState(false)
  const [draftMap, setDraftMap] = useState<Record<string, string | null>>({})

  const loadAll = useCallback(async () => {
    setLoading(true)
    try {
      const [r1, r2, r3] = await Promise.all([
        api<{ integration: IntegrationDoc }>(`/integrations/${integrationId}`),
        api<{ logs: IntegrationLog[] }>(`/integrations/${integrationId}/log`),
        api<{ items: PendingRemoval[] }>(`/integrations/${integrationId}/pending-removals`),
      ])
      setIntegration(r1.integration)
      setLogs(r2.logs || [])
      setPending(r3.items || [])
      setDraftMap(r1.integration.fieldMap || {})
      setError(null)
    } catch (e) {
      const err = e as { message?: string; status?: number }
      if (err.status === 404) setError('Integration not found.')
      else setError(err.message || 'Failed to load')
    } finally { setLoading(false) }
  }, [integrationId])

  useEffect(() => { loadAll() }, [loadAll])

  const onAction = async (action: 'poll' | 'pause' | 'unpause' | 'delete' | 'discover') => {
    setBusy(action); setError(null)
    try {
      if (action === 'delete') {
        await api(`/integrations/${integrationId}`, { method: 'DELETE' })
        navigate('/sell/integrations')
        return
      }
      await api(`/integrations/${integrationId}/${action}`, { method: 'POST' })
      await loadAll()
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || `${action} failed`)
    } finally { setBusy(null) }
  }

  const onSaveMap = async () => {
    setBusy('confirm')
    try {
      await api(`/integrations/${integrationId}/confirm`, { method: 'POST', body: { fieldMap: draftMap } })
      setEditingMap(false)
      await loadAll()
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || 'Save failed')
    } finally { setBusy(null) }
  }

  const onPendingAction = async (stockId: string, action: 'accept' | 'reject') => {
    setBusy(`${action}-${stockId}`)
    try {
      await api(`/integrations/${integrationId}/pending-removals/${stockId}/${action}`, { method: 'POST' })
      await loadAll()
    } catch (e) {
      const err = e as { message?: string }
      setError(err.message || `${action} failed`)
    } finally { setBusy(null) }
  }

  if (loading) return <AppLayout><div className="mx-auto max-w-5xl px-6 py-10"><div className="card animate-pulse h-40" /></div></AppLayout>
  if (error || !integration) return <AppLayout><div className="mx-auto max-w-5xl px-6 py-10"><p className="text-sm text-danger">{error}</p><Link to="/sell/integrations" className="btn-secondary mt-4 inline-block">Back</Link></div></AppLayout>

  return (
    <AppLayout>
      <section className="mx-auto max-w-5xl px-6 py-10">
        <Link to="/sell/integrations" className="text-xs text-primary hover:underline">← All integrations</Link>
        <div className="flex items-end justify-between gap-4 mt-2 mb-6 flex-wrap">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-primary">{integration.kind}</p>
            <h1 className="text-2xl font-bold text-ink mt-1">{integration.label || prettyKind(integration.kind)}</h1>
            <p className="text-xs text-soft mt-1">
              Cadence · every {integration.cadence.hours}h · created {new Date(integration.createdAt).toLocaleDateString()}
            </p>
          </div>
          <StatusBadge status={integration.status} />
        </div>

        {error && <p className="mb-4 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2">{error}</p>}

        <div className="card mb-6" data-testid="integration-actions">
          <p className="text-xs font-semibold text-ink uppercase tracking-wide mb-3">Actions</p>
          <div className="flex gap-2 flex-wrap">
            <button className="btn-primary text-xs" disabled={!!busy} onClick={() => onAction('poll')} data-testid="action-poll">
              {busy === 'poll' ? 'Polling…' : 'Run sync now'}
            </button>
            {integration.status === 'paused' ? (
              <button className="btn-secondary text-xs" disabled={!!busy} onClick={() => onAction('unpause')}>Resume</button>
            ) : (
              <button className="btn-secondary text-xs" disabled={!!busy} onClick={() => onAction('pause')}>Pause</button>
            )}
            <button className="btn-secondary text-xs" disabled={!!busy} onClick={() => onAction('discover')}>Re-run discovery</button>
            <button className="btn-secondary text-xs" disabled={!!busy} onClick={() => { if (confirm('Delete this integration?')) onAction('delete') }}>
              Delete
            </button>
          </div>
        </div>

        <div className="card mb-6">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-ink uppercase tracking-wide">Field map</p>
            <button onClick={() => setEditingMap((v) => !v)} className="btn-ghost text-xs" data-testid="edit-fieldmap">
              {editingMap ? 'Cancel' : 'Edit'}
            </button>
          </div>
          {editingMap ? (
            <>
              <FieldMapEditor map={draftMap} onChange={setDraftMap} sampleRow={(integration.sampleRow as Record<string, unknown>) || undefined} />
              <button className="btn-primary text-xs mt-4" disabled={busy === 'confirm'} onClick={onSaveMap}>
                {busy === 'confirm' ? 'Saving…' : 'Save map'}
              </button>
            </>
          ) : (
            <ul className="divide-y divide-border text-xs">
              {Object.entries(integration.fieldMap).map(([k, v]) => (
                <li key={k} className="py-2 flex items-center justify-between">
                  <span className="font-mono text-ink">{k}</span>
                  <span className="text-soft">→ {v || '—'}</span>
                </li>
              ))}
              {Object.keys(integration.fieldMap).length === 0 && (
                <li className="text-soft py-3">No field map yet — re-run discovery.</li>
              )}
            </ul>
          )}
        </div>

        {pending.length > 0 && (
          <div className="card mb-6" data-testid="pending-removals-list">
            <p className="text-xs font-semibold text-ink uppercase tracking-wide mb-3">
              Pending removals ({pending.length})
            </p>
            <p className="text-xs text-soft mb-4">
              These stones disappeared from your latest poll. Confirm each one to mark unavailable, or reject to keep listed.
            </p>
            <ul className="divide-y divide-border">
              {pending.map((p) => (
                <li key={p.stockId} className="py-2 flex items-center justify-between gap-2" data-testid="pending-removal-row" data-stock-id={p.stockId}>
                  <div>
                    <p className="text-sm font-mono text-ink">{p.stockId}</p>
                    <p className="text-[11px] text-soft">missing from {p.missCount} poll{p.missCount === 1 ? '' : 's'}</p>
                  </div>
                  <div className="flex gap-2">
                    <button className="btn-primary text-xs" disabled={!!busy} onClick={() => onPendingAction(p.stockId, 'accept')} data-testid={`accept-${p.stockId}`}>
                      Mark unavailable
                    </button>
                    <button className="btn-secondary text-xs" disabled={!!busy} onClick={() => onPendingAction(p.stockId, 'reject')}>
                      Keep listed
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="card">
          <p className="text-xs font-semibold text-ink uppercase tracking-wide mb-3">Recent polls</p>
          {logs.length === 0 ? (
            <p className="text-xs text-soft">No polls run yet.</p>
          ) : (
            <ul className="divide-y divide-border text-xs">
              {logs.map((l) => (
                <li key={l.logId} className="py-2 flex items-center justify-between gap-3">
                  <div>
                    <p className="text-ink">{new Date(l.ts).toLocaleString()} <span className="text-soft">· {l.trigger}</span></p>
                    <p className="text-soft">
                      +{l.added} added · ~{l.updated} updated · queued {l.removedQueued} removals
                      {l.imageAttached ? ` · 📷 ${l.imageAttached}` : ''}
                      {l.errors.length > 0 ? ` · ⚠ ${l.errors.length} errors` : ''}
                    </p>
                  </div>
                  <span className="text-[10px] text-soft">{l.durationMs}ms</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </AppLayout>
  )
}
